import { type RoomEvent, idempotencyKey } from "@br9704/aethereum-shared";

/**
 * Mirror of structured events from the room server to the hosted sync API
 * (which persists them to Supabase → dashboard). Env-gated. A mirror failure
 * NEVER affects the live room, but — unlike the old fire-and-forget send — a
 * transient failure no longer drops the event forever (bug B11). Events queue
 * in a DURABLE OUTBOX drained with retry + backoff; the outbox is exposed via
 * `pending()`/`hydrate()` so the room server can persist it and replay the tail
 * after a restart. The idempotency key is shared with the hosted MCP store so
 * writes dedupe (a replayed-after-restart event that already landed is a no-op).
 *
 *   AETHEREUM_SYNC_API_URL  e.g. https://www.aethereum.dev
 *   AETHEREUM_SYNC_TOKEN    the room's api_token (raw; the API stores only its hash)
 */
export interface PendingMirror {
  key: string;
  roomId: string;
  event: RoomEvent;
  attempts: number;
}

export interface Mirror {
  enabled: boolean;
  /** Enqueue an event for durable, retrying delivery. Returns immediately. */
  send(roomId: string, event: RoomEvent): void;
  /** The undelivered outbox, for persistence. */
  pending(): PendingMirror[];
  /** Restore a persisted outbox on boot and kick the drain. */
  hydrate(items: PendingMirror[]): void;
  /** Await the queue draining to empty (or giving up) — for shutdown/tests. */
  flush(): Promise<void>;
  /** Called whenever the outbox changes, so the caller can persist it. */
  onChange?: (pending: PendingMirror[]) => void;
  /** Stop the retry timer. */
  stop(): void;
}

const MAX_ATTEMPTS = 8; // ~ up to the backoff ceiling, then park for the periodic sweep
const RETRY_SWEEP_MS = 15_000; // re-attempt a stuck queue on this cadence

export interface MirrorOptions {
  /** Base backoff (ms) for the exponential retry. Overridable for fast tests. */
  retryBaseMs?: number;
  /** Periodic re-drain cadence for a parked queue. */
  sweepMs?: number;
}

export function createMirror(
  env: { AETHEREUM_SYNC_API_URL?: string; AETHEREUM_SYNC_TOKEN?: string } = process.env,
  fetchImpl: typeof fetch = fetch,
  opts: MirrorOptions = {},
): Mirror {
  const url = env.AETHEREUM_SYNC_API_URL;
  const token = env.AETHEREUM_SYNC_TOKEN;
  const enabled = Boolean(url && token);
  const retryBaseMs = opts.retryBaseMs ?? 1000;
  const sweepMs = opts.sweepMs ?? RETRY_SWEEP_MS;

  const queue: PendingMirror[] = [];
  let drainPromise: Promise<void> | null = null;
  let sweep: ReturnType<typeof setInterval> | null = null;

  const mirror: Mirror = {
    enabled,
    send(roomId, event) {
      if (!enabled) return;
      queue.push({ key: idempotencyKey(roomId, event), roomId, event, attempts: 0 });
      mirror.onChange?.(queue);
      kick();
    },
    pending: () => queue.slice(),
    hydrate(items) {
      if (!enabled || !Array.isArray(items)) return;
      for (const it of items) queue.push({ ...it, attempts: 0 });
      if (queue.length) kick();
    },
    async flush() {
      await kick();
    },
    stop() {
      if (sweep) {
        clearInterval(sweep);
        sweep = null;
      }
    },
  };

  /** Ensure exactly one drain runs; return the in-flight drain so callers can await it. */
  function kick(): Promise<void> {
    if (!drainPromise) {
      drainPromise = drain().finally(() => {
        drainPromise = null;
      });
    }
    return drainPromise;
  }

  function ensureSweep(): void {
    if (sweep || !enabled) return;
    sweep = setInterval(() => {
      if (queue.length) kick();
    }, sweepMs);
    // Don't keep the process alive just for the retry sweep.
    (sweep as unknown as { unref?: () => void }).unref?.();
  }

  async function postOne(item: PendingMirror): Promise<boolean> {
    try {
      const res = await fetchImpl(`${url!.replace(/\/$/, "")}/api/sync/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ events: [{ idempotencyKey: item.key, event: item.event }] }),
      });
      return res.ok;
    } catch {
      return false; // network error — keep it queued
    }
  }

  async function drain(): Promise<void> {
    if (!enabled) return;
    while (queue.length) {
      const item = queue[0]!;
      const ok = await postOne(item);
      if (ok) {
        queue.shift();
        mirror.onChange?.(queue);
        continue;
      }
      item.attempts += 1;
      if (item.attempts >= MAX_ATTEMPTS) {
        // Give up on the in-line drain; leave it queued for the periodic sweep
        // (and for persistence + boot replay). Never block the room.
        ensureSweep();
        break;
      }
      // Exponential backoff, capped, before retrying the head of the queue.
      const delay = Math.min(retryBaseMs * 2 ** (item.attempts - 1), 30_000);
      if (delay > 0) await new Promise((r) => setTimeout(r, delay));
    }
  }

  return mirror;
}
